export const LINE_BREAK_THRESHOLD = 2;

export const getAllTextNodes = (element: Node): Text[] => {
  const nodes: Text[] = [];
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      nodes.push(node as Text);
    } else {
      node.childNodes.forEach(walk);
    }
  };
  walk(element);
  return nodes;
};

export const getActualLineHeight = (element: HTMLElement, doc: Document): number => {
  const nodes = getAllTextNodes(element);
  if (nodes.length > 0) {
    const range = doc.createRange();
    range.setStart(nodes[0], 0);
    range.setEnd(nodes[0], Math.min(1, nodes[0].length));
    const h = range.getBoundingClientRect().height;
    if (h > 0) return h;
  }
  const style = window.getComputedStyle(element);
  const parsed = parseFloat(style.lineHeight);
  return !isNaN(parsed) && parsed < 100 ? parsed : parseFloat(style.fontSize) * 1.4;
};

export const createHighlightRect = (
  x: number,
  y: number,
  width: number,
  height: number,
  fillColor: string,
  fillOpacity: string,
  blendMode: string,
): SVGRectElement => {
  const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  rect.setAttribute('x', x.toString());
  rect.setAttribute('y', y.toString());
  rect.setAttribute('width', width.toString());
  rect.setAttribute('height', height.toString());
  rect.setAttribute('fill', fillColor);
  rect.setAttribute('fill-opacity', fillOpacity);
  rect.setAttribute('style', `mix-blend-mode: ${blendMode};`);
  return rect;
};

/**
 * Draws pixel-perfect SVG rects for user highlights and underlines.
 * Strips epub.js default fill attributes to prevent Safari rendering bugs.
 */
export const applyCustomAnnotationRects = (
  container: SVGGElement,
  range: Range,
  type: 'highlight' | 'underline',
  color: string,
  isDark: boolean,
): void => {
  container.querySelectorAll('rect').forEach((r) => r.remove());
  container.removeAttribute('fill');
  container.removeAttribute('fill-opacity');
  container.style.mixBlendMode = '';

  const clientRects = Array.from(range.getClientRects()).filter((r) => r.width > 0 && r.height > 0);
  if (!clientRects.length) return;

  const THRESHOLD = 2;
  const lines = new Map<number, { minX: number; maxX: number; top: number; bottom: number }>();

  clientRects.forEach((rect) => {
    const key = Math.round(rect.bottom);
    let matched: number | undefined;
    for (const k of lines.keys()) {
      if (Math.abs(k - key) <= THRESHOLD) {
        matched = k;
        break;
      }
    }
    const k = matched ?? key;
    const existing = lines.get(k);
    if (existing) {
      existing.minX = Math.min(existing.minX, rect.left);
      existing.maxX = Math.max(existing.maxX, rect.right);
      existing.top = Math.min(existing.top, rect.top);
      existing.bottom = Math.max(existing.bottom, rect.bottom);
    } else {
      lines.set(k, { minX: rect.left, maxX: rect.right, top: rect.top, bottom: rect.bottom });
    }
  });

  const isUnderline = type === 'underline';
  const fillOpacity = isUnderline ? '1' : '0.4';
  const blendMode = isDark ? 'screen' : 'multiply';

  lines.forEach(({ minX, maxX, top, bottom }) => {
    const rectHeight = isUnderline ? 2 : bottom - top;
    const rectY = isUnderline ? bottom - 2 : top;
    container.appendChild(createHighlightRect(minX, rectY, maxX - minX, rectHeight, color, fillOpacity, blendMode));
  });
};

export const measureTextLines = (element: HTMLElement, doc: Document) => {
  const textNodes = getAllTextNodes(element);
  const lines: Array<{ y: number; startX: number; endX: number }> = [];
  let currentY = -1;

  textNodes.forEach((node) => {
    const range = doc.createRange();
    for (let i = 0; i < node.length; i++) {
      range.setStart(node, i);
      range.setEnd(node, i + 1);
      const rect = range.getBoundingClientRect();
      if (rect.width === 0) continue;

      const isNewLine = currentY === -1 || Math.abs(rect.top - currentY) > LINE_BREAK_THRESHOLD;
      if (isNewLine) {
        currentY = rect.top;
        lines.push({ y: rect.top, startX: rect.left, endX: rect.right });
      } else {
        const lastLine = lines[lines.length - 1];
        lastLine.endX = Math.max(lastLine.endX, rect.right);
      }
    }
  });
  return lines;
};
